import { z } from "zod";
/*
 * Custo estimado da infraestrutura (Administração). A versioned cost model of
 * the Dolphin stack (GCP project backchannel-agent-dolphin, southamerica-east1)
 * plus a pure estimator. It is an ESTIMATE from public list prices, never an
 * invoice: no discounts, credits, taxes or committed-use contracts.
 *
 * Three inputs, each easy to update on its own:
 * 1. INFRA_UNIT_PRICES: public list prices with source URL and check date.
 * 2. INFRA_TERRAFORM_SNAPSHOT: the billable sizing read from infra/terraform
 *    and the GCP_TF_CURRENT_VARS_DOLPHIN desired state. The contract test
 *    `infra-cost.spec.ts` parses the Terraform files and fails, naming the
 *    key, whenever a Cloud Run, job, Cloud SQL or VM value drifts. After a
 *    scaling change, update the snapshot value the test names and bump
 *    INFRA_COST_MODEL_VERSION.
 * 3. Usage drivers: per-month activity. IDLE and HIGH are documented
 *    profiles; TYPICAL takes the app's own records (lots, rows, research and
 *    NF-e spend, browser agent steps) over the last 30 days when available.
 *
 * Billing-account free tiers (Cloud Run, Scheduler, Secret Manager, API
 * Gateway, Artifact Registry) are NOT subtracted: Backchannel's billing account
 * also pays for other projects, so they cannot be assumed for this one. The
 * per-project Cloud Logging allowance (50 GiB) is subtracted.
 */
export const INFRA_COST_MODEL_VERSION = "2026.10.15";
export const INFRA_COST_REGION = "southamerica-east1";
/** Average month: 365 days / 12. */
export const HOURS_PER_MONTH = 730;
export const SECONDS_PER_MONTH = HOURS_PER_MONTH * 3600;
const DAYS_PER_MONTH = 365 / 12;
const MINUTES_PER_MONTH = DAYS_PER_MONTH * 24 * 60;
/* -- Unit prices ------------------------------------------------------------- */
export const infraPriceScopeSchema = z.enum(["REGIONAL", "GLOBAL", "OTHER_REGION"]);
export const infraUnitPriceSchema = z
    .object({
    id: z.string().min(1).max(80),
    /** Google's SKU wording (English, as on the pricing page). */
    sku: z.string().min(1).max(200),
    usd: z.number().nonnegative(),
    unit: z.string().min(1).max(60),
    /** REGIONAL: read for southamerica-east1; GLOBAL: one price everywhere. */
    scope: infraPriceScopeSchema,
    sourceUrl: z.string().url(),
    checkedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    note: z.string().max(400).optional(),
})
    .strict();
const CHECKED_ON = "2026-10-02";
const RUN = "https://cloud.google.com/run/pricing";
const SQL = "https://cloud.google.com/sql/pricing";
const VM = "https://cloud.google.com/products/compute/pricing/general-purpose";
const DISK = "https://cloud.google.com/compute/disks-image-pricing";
const NET = "https://cloud.google.com/vpc/network-pricing";
const NAT = "https://cloud.google.com/nat/pricing";
const IP = "https://cloud.google.com/vpc/network-pricing#ipaddress";
const GCS = "https://cloud.google.com/storage/pricing";
const GATEWAY = "https://cloud.google.com/api-gateway/docs/pricing";
const PUBSUB = "https://cloud.google.com/pubsub/pricing";
const SCHEDULER = "https://cloud.google.com/scheduler/pricing";
const SECRETS = "https://cloud.google.com/secret-manager/pricing";
const REGISTRY = "https://cloud.google.com/artifact-registry/pricing";
const LOGGING = "https://cloud.google.com/stackdriver/pricing";
const FIREBASE = "https://firebase.google.com/pricing";
const VERTEX = "https://cloud.google.com/vertex-ai/generative-ai/pricing";
function price(id, sku, usd, unit, scope, sourceUrl, note) {
    return { id, sku, usd, unit, scope, sourceUrl, checkedOn: CHECKED_ON, ...(note ? { note } : {}) };
}
/**
 * Public list prices, USD. Update a value, its note and CHECKED_ON together.
 * PRICE_IDS below is the closed set the estimator reads.
 */
export const INFRA_UNIT_PRICES = {
    runCpuActive: price("runCpuActive", "Cloud Run services (request-based) CPU, Tier 2", 0.0000336, "vCPU-second", "REGIONAL", RUN, "southamerica-east1 is a Tier 2 Cloud Run region."),
    runMemoryActive: price("runMemoryActive", "Cloud Run services (request-based) memory, Tier 2", 0.0000035, "GiB-second", "REGIONAL", RUN),
    runCpuIdle: price("runCpuIdle", "Cloud Run idle min-instance CPU (request-based), Tier 2", 0.0000035, "vCPU-second", "REGIONAL", RUN, "Idle min instances of request-based services bill CPU at the reduced idle rate."),
    runMemoryIdle: price("runMemoryIdle", "Cloud Run idle min-instance memory (request-based), Tier 2", 0.0000035, "GiB-second", "REGIONAL", RUN),
    runCpuInstance: price("runCpuInstance", "Cloud Run services (instance-based) CPU, Tier 2", 0.0000216, "vCPU-second", "REGIONAL", RUN, "Services with CPU always allocated bill the whole instance lifetime, idle or busy."),
    runMemoryInstance: price("runMemoryInstance", "Cloud Run services (instance-based) memory, Tier 2", 0.0000024, "GiB-second", "REGIONAL", RUN),
    runRequests: price("runRequests", "Cloud Run requests", 0.4, "million requests", "GLOBAL", RUN),
    runJobCpu: price("runJobCpu", "Cloud Run jobs (instance-based) CPU, Tier 2", 0.0000216, "vCPU-second", "REGIONAL", RUN, "Jobs bill the whole execution, start-up included."),
    runJobMemory: price("runJobMemory", "Cloud Run jobs (instance-based) memory, Tier 2", 0.0000024, "GiB-second", "REGIONAL", RUN),
    sqlVcpu: price("sqlVcpu", "Cloud SQL Enterprise edition, General Purpose vCPU (zonal)", 0.062, "vCPU-hour", "REGIONAL", SQL, "HA (REGIONAL) doubles vCPU, memory and storage prices."),
    sqlMemory: price("sqlMemory", "Cloud SQL Enterprise edition, General Purpose memory (zonal)", 0.0105, "GB-hour", "REGIONAL", SQL),
    sqlSsd: price("sqlSsd", "Cloud SQL SSD storage (zonal)", 0.255, "GB-month", "REGIONAL", SQL),
    sqlBackup: price("sqlBackup", "Cloud SQL backup storage", 0.12, "GB-month", "REGIONAL", SQL),
    e2Standard4: price("e2Standard4", "Compute Engine e2-standard-4 on-demand", 0.2127408, "hour", "REGIONAL", VM),
    e2Micro: price("e2Micro", "Compute Engine e2-micro on-demand", 0.0132963, "hour", "REGIONAL", VM),
    pdBalanced: price("pdBalanced", "Balanced persistent disk (pd-balanced)", 0.15, "GB-month", "REGIONAL", DISK),
    natGateway: price("natGateway", "Cloud NAT gateway uptime (per assigned VM, up to 32)", 0.0014, "VM-hour", "GLOBAL", NAT),
    natData: price("natData", "Cloud NAT data processing", 0.045, "GiB", "GLOBAL", NAT),
    externalIp: price("externalIp", "External IPv4 address in use (static or ephemeral, also each NAT IP)", 0.005, "hour", "GLOBAL", IP, "Internal IP addresses (browser agent, Twingate) are free."),
    egressInternet: price("egressInternet", "Premium Tier internet egress from São Paulo, 0-1 TiB", 0.19, "GiB", "REGIONAL", NET),
    gcsStandard: price("gcsStandard", "Cloud Storage Standard, regional", 0.035, "GB-month", "REGIONAL", GCS),
    gatewayCalls: price("gatewayCalls", "API Gateway calls (2M-1B tier)", 3, "million calls", "GLOBAL", GATEWAY),
    pubsubThroughput: price("pubsubThroughput", "Pub/Sub message delivery throughput", 40, "TiB", "GLOBAL", PUBSUB),
    schedulerJob: price("schedulerJob", "Cloud Scheduler job", 0.1, "job-month", "GLOBAL", SCHEDULER),
    secretVersion: price("secretVersion", "Secret Manager active secret version", 0.06, "version-month", "GLOBAL", SECRETS),
    secretAccess: price("secretAccess", "Secret Manager access operations", 0.03, "10,000 operations", "GLOBAL", SECRETS),
    registryStorage: price("registryStorage", "Artifact Registry storage", 0.1, "GB-month", "GLOBAL", REGISTRY),
    loggingIngest: price("loggingIngest", "Cloud Logging ingestion (after 50 GiB per project)", 0.5, "GiB", "GLOBAL", LOGGING),
    firebaseTransfer: price("firebaseTransfer", "Firebase Hosting data transfer (Blaze, after 360 MB/day)", 0.15, "GB", "GLOBAL", FIREBASE),
    geminiFlashInput: price("geminiFlashInput", "Vertex AI Gemini 2.5 Flash input", 0.3, "1M tokens", "GLOBAL", VERTEX, "The app prices research and NF-e runs with its own admin-editable price table; those recorded amounts are used when available."),
    geminiFlashOutput: price("geminiFlashOutput", "Vertex AI Gemini 2.5 Flash output", 2.5, "1M tokens", "GLOBAL", VERTEX),
    groundedPrompt: price("groundedPrompt", "Vertex AI Grounding with Google Search (Gemini 2.5)", 35, "1,000 grounded prompts", "GLOBAL", VERTEX, "Vertex AI gives 1,500 grounded prompts a day free (2.0/2.5 Flash family, shared). The app records every grounded call at this price, so recorded research spend is an upper bound."),
};
/** Converting the USD total for display only; the model is priced in USD. */
export const INFRA_EXCHANGE_RATE = {
    usdBrl: 5.2238,
    rateDate: "2026-10-02",
    source: "Banco Central do Brasil, PTAX (venda)",
    sourceUrl: "https://olinda.bcb.gov.br/olinda/servico/PTAX/versao/v1/odata/",
};
const cron = (everyMinutes) => Math.round(MINUTES_PER_MONTH / everyMinutes);
/**
 * Sizing from main @ 344b3750 plus the scaling change of 3 October 2026 (admin API
 * 2 GiB and at most 8 instances, runtime API at most 6) and the warm admin API of
 * 5 October 2026 (CPU always allocated on its minimum instance). Every Cloud Run, job,
 * Cloud SQL and VM value below is cross-checked against the Terraform text by
 * packages/contracts/test/infra-cost.spec.ts.
 */
export const INFRA_TERRAFORM_SNAPSHOT = {
    source: "infra/terraform/modules (bujosa/admin-min-instance) + environments/dolphin + GCP_TF_CURRENT_VARS_DOLPHIN",
    services: [
        {
            id: "admin-api",
            terraform: "modules/runtime/main.tf google_cloud_run_v2_service.admin_api",
            cpu: 2,
            memoryGib: 2,
            minInstances: 1,
            maxInstances: 8,
            concurrency: 80,
            cpuAlwaysAllocated: true,
        },
        {
            id: "runtime-api",
            terraform: "modules/runtime/main.tf google_cloud_run_v2_service.runtime_api",
            cpu: 2,
            memoryGib: 2,
            minInstances: 1,
            maxInstances: 6,
            concurrency: 40,
            cpuAlwaysAllocated: false,
        },
        {
            id: "web",
            terraform: "modules/runtime/main.tf google_cloud_run_v2_service.web",
            cpu: 1,
            memoryGib: 0.25,
            minInstances: 0,
            maxInstances: 5,
            concurrency: 250,
            cpuAlwaysAllocated: false,
        },
        {
            id: "worker",
            terraform: "modules/runtime/main.tf google_cloud_run_v2_service.worker (fetcher, extractor)",
            // Fetcher 1 vCPU / 1 GiB, extractor 2 vCPU / 2 GiB; the per-document
            // worker time is mostly extraction, so the model uses the extractor's.
            cpu: 2,
            memoryGib: 2,
            minInstances: 0,
            maxInstances: 5,
            concurrency: 4,
            cpuAlwaysAllocated: false,
        },
        {
            id: "browser-agent-viewer",
            terraform: "modules/runtime/browser-agent.tf google_cloud_run_v2_service.browser_agent_viewer",
            cpu: 1,
            memoryGib: 0.5,
            minInstances: 0,
            maxInstances: 1,
            concurrency: 20,
            cpuAlwaysAllocated: false,
        },
    ],
    jobs: [
        {
            id: "fiscal-task-runner",
            terraform: "modules/runtime/main.tf google_cloud_run_v2_job.jobs (default 1 vCPU / 512 MiB)",
            cpu: 1,
            memoryGib: 0.5,
            schedule: "* * * * *",
            executionsPerMonth: cron(1),
            idleSecondsPerExecution: 15,
        },
        {
            id: "calculator-research",
            terraform: "modules/runtime/main.tf google_cloud_run_v2_job.jobs (default 1 vCPU / 512 MiB)",
            cpu: 1,
            memoryGib: 0.5,
            schedule: "*/2 * * * *",
            executionsPerMonth: cron(2),
            idleSecondsPerExecution: 15,
        },
        {
            id: "web-research-runner",
            terraform: "modules/runtime/web-research.tf google_cloud_run_v2_job.web_research_runner",
            cpu: 2,
            memoryGib: 2,
            schedule: "*/5 * * * *",
            executionsPerMonth: cron(5),
            idleSecondsPerExecution: 20,
        },
        {
            id: "nfe-analysis-runner",
            terraform: "modules/runtime/nfe-analysis.tf google_cloud_run_v2_job.nfe_analysis_runner",
            cpu: 1,
            memoryGib: 1,
            schedule: "*/5 * * * *",
            executionsPerMonth: cron(5),
            idleSecondsPerExecution: 15,
        },
        {
            id: "agent-runner",
            terraform: "modules/runtime/agent-runner.tf google_cloud_run_v2_job.agent_runner",
            cpu: 2,
            memoryGib: 2,
            schedule: "*/5 * * * *",
            executionsPerMonth: cron(5),
            idleSecondsPerExecution: 20,
        },
        {
            id: "daily-jobs",
            terraform: "agent-retention 06:15, fiscal-facts-maintenance 05:30 (UTC) and the daily discovery tick",
            cpu: 1,
            memoryGib: 0.5,
            schedule: "daily",
            executionsPerMonth: Math.round(DAYS_PER_MONTH * 2),
            idleSecondsPerExecution: 120,
        },
    ],
    cloudSql: {
        terraform: "environments/dolphin/main.tf module.platform database_*",
        tier: "db-custom-1-3840",
        edition: "ENTERPRISE",
        vcpu: 1,
        memoryGb: 3.75,
        availability: "ZONAL",
        diskGb: 25,
        retainedBackups: 14,
        pitrDays: 7,
    },
    vms: [
        {
            id: "browser-agent",
            terraform: "modules/runtime/browser-agent.tf google_compute_instance.browser_agent",
            machineType: "e2-standard-4",
            priceId: "e2Standard4",
            diskGb: 60 + 20,
        },
        {
            id: "twingate",
            terraform: "modules/twingate/main.tf google_compute_instance.connector",
            machineType: "e2-micro",
            priceId: "e2Micro",
            diskGb: 10,
        },
    ],
    nat: { gateways: 2, assignedVms: 2 },
    externalIps: 2,
    schedulerJobs: 8,
    secrets: { secrets: 12, activeVersions: 16 },
    gcsBuckets: 5,
    pubsubTopics: 5,
    apiGatewayRegion: "us-east1",
    artifactRegistry: { repositories: 1, cleanupPolicy: false },
};
/* -- Usage drivers ------------------------------------------------------------ */
export const infraScenarioIdSchema = z.enum(["IDLE", "TYPICAL", "HIGH"]);
/** Monthly activity that turns into usage-based cost. */
export const infraUsageDriversSchema = z
    .object({
    /** Admin + runtime API requests (each one also crosses API Gateway when it is an admin call). */
    apiRequests: z.number().nonnegative(),
    /** Share of apiRequests that are admin (browser) calls through API Gateway. */
    gatewayShare: z.number().min(0).max(1),
    apiAvgLatencySeconds: z.number().nonnegative(),
    /** Average requests in flight on a busy instance (how well concurrency packs). */
    apiInFlightPerInstance: z.number().positive(),
    lotRows: z.number().nonnegative(),
    /** vCPU-seconds to calculate one lot row (task runner and API). */
    cpuSecondsPerLotRow: z.number().nonnegative(),
    documentsFetched: z.number().nonnegative(),
    workerSecondsPerDocument: z.number().nonnegative(),
    /** Hours the web research runner works (2 vCPU) beyond its idle ticks. */
    webResearchActiveHours: z.number().nonnegative(),
    /** Gemini spend the app recorded (or a budgeted figure), USD. */
    researchAiUsd: z.number().nonnegative(),
    nfeAiUsd: z.number().nonnegative(),
    browserAgentSteps: z.number().nonnegative(),
    browserAgentInputTokensPerStep: z.number().nonnegative(),
    browserAgentOutputTokensPerStep: z.number().nonnegative(),
    /** Internet egress (API responses, downloads, fetches through NAT are counted in natGib). */
    egressGib: z.number().nonnegative(),
    natGib: z.number().nonnegative(),
    firebaseTransferGb: z.number().nonnegative(),
    logsGib: z.number().nonnegative(),
    gcsStorageGb: z.number().nonnegative(),
    sqlBackupGb: z.number().nonnegative(),
    registryGb: z.number().nonnegative(),
    pubsubGib: z.number().nonnegative(),
    secretAccesses: z.number().nonnegative(),
    /** HIGH only: the Cloud SQL size the load needs (the current tier cannot hold it). */
    cloudSqlOverride: z
        .object({
        vcpu: z.number().positive(),
        memoryGb: z.number().positive(),
        availability: z.enum(["ZONAL", "REGIONAL"]),
        diskGb: z.number().positive(),
    })
        .strict()
        .optional(),
})
    .strict();
/**
 * Documented profiles. IDLE: nothing but schedulers, min instances and
 * storage. TYPICAL: about ten operators on business days (replaced by the
 * live records where the app has them). HIGH: about 2,000 concurrent users
 * (the scaling work in progress), AI lanes at their monthly budget caps.
 */
export const INFRA_USAGE_PROFILES = {
    IDLE: {
        apiRequests: 60_000,
        gatewayShare: 0.1,
        apiAvgLatencySeconds: 0.08,
        apiInFlightPerInstance: 1,
        lotRows: 0,
        cpuSecondsPerLotRow: 0.05,
        documentsFetched: 300,
        workerSecondsPerDocument: 6,
        webResearchActiveHours: 0,
        researchAiUsd: 0,
        nfeAiUsd: 0,
        browserAgentSteps: 0,
        browserAgentInputTokensPerStep: 4_000,
        browserAgentOutputTokensPerStep: 300,
        egressGib: 1,
        natGib: 2,
        firebaseTransferGb: 0,
        logsGib: 15,
        gcsStorageGb: 20,
        sqlBackupGb: 30,
        registryGb: 40,
        pubsubGib: 1,
        secretAccesses: 20_000,
    },
    TYPICAL: {
        apiRequests: 400_000,
        gatewayShare: 0.6,
        apiAvgLatencySeconds: 0.2,
        apiInFlightPerInstance: 2,
        lotRows: 50_000,
        cpuSecondsPerLotRow: 0.05,
        documentsFetched: 3_000,
        workerSecondsPerDocument: 6,
        webResearchActiveHours: 60,
        researchAiUsd: 60,
        nfeAiUsd: 5,
        browserAgentSteps: 3_000,
        browserAgentInputTokensPerStep: 4_000,
        browserAgentOutputTokensPerStep: 300,
        egressGib: 10,
        natGib: 15,
        firebaseTransferGb: 2,
        logsGib: 35,
        gcsStorageGb: 40,
        sqlBackupGb: 40,
        registryGb: 40,
        pubsubGib: 2,
        secretAccesses: 60_000,
    },
    HIGH: {
        // 2,000 concurrent users x one call every 20 s x 10 h x 22 business days.
        apiRequests: 80_000_000,
        gatewayShare: 0.9,
        apiAvgLatencySeconds: 0.25,
        apiInFlightPerInstance: 20,
        lotRows: 2_000_000,
        cpuSecondsPerLotRow: 0.05,
        documentsFetched: 20_000,
        workerSecondsPerDocument: 6,
        webResearchActiveHours: 300,
        researchAiUsd: 300,
        nfeAiUsd: 20,
        browserAgentSteps: 20_000,
        browserAgentInputTokensPerStep: 4_000,
        browserAgentOutputTokensPerStep: 300,
        // 80M responses x ~12 KB.
        egressGib: 900,
        natGib: 100,
        // 2,000 users x ~2 MB of app bundle per business day.
        firebaseTransferGb: 88,
        logsGib: 160,
        gcsStorageGb: 200,
        sqlBackupGb: 200,
        registryGb: 40,
        pubsubGib: 10,
        secretAccesses: 500_000,
        cloudSqlOverride: { vcpu: 4, memoryGb: 16, availability: "REGIONAL", diskGb: 100 },
    },
};
/* -- Live usage (the app's own records) --------------------------------------- */
export const infraLiveUsageSchema = z
    .object({
    status: z.enum(["LIVE", "UNAVAILABLE"]),
    windowDays: z.number().int().positive(),
    from: z.string().datetime().nullable(),
    to: z.string().datetime().nullable(),
    lots: z.number().int().nonnegative(),
    lotRows: z.number().int().nonnegative(),
    calculations: z.number().int().nonnegative(),
    researchUnits: z.number().int().nonnegative(),
    groundedCalls: z.number().int().nonnegative(),
    researchCostUsd: z.number().nonnegative(),
    researchDocuments: z.number().int().nonnegative(),
    nfeRuns: z.number().int().nonnegative(),
    nfeCostUsd: z.number().nonnegative(),
    browserAgentRuns: z.number().int().nonnegative(),
    browserAgentSteps: z.number().int().nonnegative(),
    /** Admin-set monthly AI caps (web research, NF-e review), USD. */
    researchMonthlyBudgetUsd: z.number().nonnegative(),
    nfeMonthlyBudgetUsd: z.number().nonnegative(),
})
    .strict();
export function emptyInfraLiveUsage(windowDays = 30) {
    return {
        status: "UNAVAILABLE",
        windowDays,
        from: null,
        to: null,
        lots: 0,
        lotRows: 0,
        calculations: 0,
        researchUnits: 0,
        groundedCalls: 0,
        researchCostUsd: 0,
        researchDocuments: 0,
        nfeRuns: 0,
        nfeCostUsd: 0,
        browserAgentRuns: 0,
        browserAgentSteps: 0,
        researchMonthlyBudgetUsd: 300,
        nfeMonthlyBudgetUsd: 20,
    };
}
/** Scales a 30-day (or other) window to an average month. */
function perMonth(value, windowDays) {
    return windowDays > 0 ? (value * DAYS_PER_MONTH) / windowDays : value;
}
/** TYPICAL drivers: the profile, with every recorded figure replaced by the app's records. */
export function typicalDriversFromUsage(usage) {
    const base = INFRA_USAGE_PROFILES.TYPICAL;
    if (usage.status !== "LIVE")
        return base;
    const days = usage.windowDays;
    const researchUnits = perMonth(usage.researchUnits, days);
    return {
        ...base,
        lotRows: perMonth(usage.lotRows, days),
        documentsFetched: Math.max(perMonth(usage.researchDocuments, days), 300),
        // ASSUMPTION: ~30 s per unit spread over ~8 concurrent slots.
        webResearchActiveHours: (researchUnits * 30) / 8 / 3600,
        researchAiUsd: perMonth(usage.researchCostUsd, days),
        nfeAiUsd: perMonth(usage.nfeCostUsd, days),
        browserAgentSteps: perMonth(usage.browserAgentSteps, days),
    };
}
/** HIGH drivers: the profile, with the AI lanes at the admin-set monthly caps. */
export function highDriversFromUsage(usage) {
    const base = INFRA_USAGE_PROFILES.HIGH;
    return {
        ...base,
        researchAiUsd: Math.max(usage.researchMonthlyBudgetUsd, base.researchAiUsd),
        nfeAiUsd: Math.max(usage.nfeMonthlyBudgetUsd, base.nfeAiUsd),
    };
}
/* -- Estimate ----------------------------------------------------------------- */
export const infraServiceSchema = z.enum([
    "CLOUD_RUN",
    "CLOUD_RUN_JOBS",
    "CLOUD_SQL",
    "COMPUTE_ENGINE",
    "NETWORKING",
    "API_GATEWAY",
    "CLOUD_STORAGE",
    "ARTIFACT_REGISTRY",
    "PUBSUB",
    "CLOUD_SCHEDULER",
    "SECRET_MANAGER",
    "CLOUD_LOGGING",
    "FIREBASE_HOSTING",
    "VERTEX_AI",
]);
export const infraCostKindSchema = z.enum(["FIXED", "VARIABLE"]);
export const infraLineIdSchema = z.enum([
    "runMinInstances",
    "runServiceRequests",
    "runWorkers",
    "jobsScheduledTicks",
    "jobsLotRows",
    "jobsWebResearch",
    "sqlInstance",
    "sqlStorage",
    "sqlBackups",
    "vmBrowserAgent",
    "vmTwingate",
    "natGateways",
    "natData",
    "externalIps",
    "egress",
    "apiGateway",
    "gcsStorage",
    "registryStorage",
    "pubsub",
    "scheduler",
    "secretManager",
    "logging",
    "firebaseHosting",
    "geminiResearch",
    "geminiNfe",
    "geminiBrowserAgent",
]);
export const infraCostLineSchema = z
    .object({
    id: infraLineIdSchema,
    service: infraServiceSchema,
    kind: infraCostKindSchema,
    usd: z.number().nonnegative(),
    /** Billed quantity in `unit` (after any per-project allowance). */
    quantity: z.number().nonnegative(),
    unit: z.string().min(1).max(60),
    priceIds: z.array(z.string()).min(1),
    /** RECORDED: from the app's own records; MODELED: from a documented assumption. */
    basis: z.enum(["TERRAFORM", "RECORDED", "MODELED"]),
})
    .strict();
export const infraScenarioEstimateSchema = z
    .object({
    id: infraScenarioIdSchema,
    totalUsd: z.number().nonnegative(),
    fixedUsd: z.number().nonnegative(),
    variableUsd: z.number().nonnegative(),
    totalBrl: z.number().nonnegative(),
    lines: z.array(infraCostLineSchema),
    drivers: infraUsageDriversSchema,
})
    .strict();
const round2 = (value) => Math.round(value * 100) / 100;
function line(id, service, kind, quantity, unit, usd, priceIds, basis) {
    return {
        id,
        service,
        kind,
        quantity: round2(Math.max(0, quantity)),
        unit,
        usd: round2(Math.max(0, usd)),
        priceIds,
        basis,
    };
}
/**
 * Prices one scenario. Pure: the same snapshot, prices and drivers always give
 * the same lines. `recorded` marks the AI and lot figures that came from the
 * app's own records (TYPICAL with live usage).
 */
export function estimateInfraScenario(id, drivers, options = {}) {
    const s = options.snapshot ?? INFRA_TERRAFORM_SNAPSHOT;
    const p = options.prices ?? INFRA_UNIT_PRICES;
    const fx = options.usdBrl ?? INFRA_EXCHANGE_RATE.usdBrl;
    const usageBasis = options.recorded ? "RECORDED" : "MODELED";
    const u = (key) => p[key].usd;
    const lines = [];
    const service = (serviceId) => s.services.find((entry) => entry.id === serviceId);
    // Cloud Run: min instances warm all month. Request-based services pay the
    // idle rate; a service with CPU always allocated pays the instance-based rate.
    const apis = ["admin-api", "runtime-api"]
        .map(service)
        .filter((entry) => Boolean(entry));
    const warmSeconds = (entry, size) => entry.minInstances * size * SECONDS_PER_MONTH;
    const warmVcpuSeconds = apis.reduce((sum, entry) => sum + warmSeconds(entry, entry.cpu), 0);
    const warmUsd = apis.reduce((sum, entry) => sum +
        (entry.cpuAlwaysAllocated
            ? warmSeconds(entry, entry.cpu) * u("runCpuInstance") +
                warmSeconds(entry, entry.memoryGib) * u("runMemoryInstance")
            : warmSeconds(entry, entry.cpu) * u("runCpuIdle") +
                warmSeconds(entry, entry.memoryGib) * u("runMemoryIdle")), 0);
    const warmPriceIds = [
        ...(apis.some((entry) => !entry.cpuAlwaysAllocated)
            ? ["runCpuIdle", "runMemoryIdle"]
            : []),
        ...(apis.some((entry) => entry.cpuAlwaysAllocated)
            ? ["runCpuInstance", "runMemoryInstance"]
            : []),
    ];
    lines.push(line("runMinInstances", "CLOUD_RUN", "FIXED", warmVcpuSeconds, "vCPU-s", warmUsd, warmPriceIds.length ? warmPriceIds : ["runCpuIdle", "runMemoryIdle"], "TERRAFORM"));
    // Cloud Run: active request time on the APIs (average of the two shapes).
    const apiCpu = apis.length ? apis.reduce((sum, e) => sum + e.cpu, 0) / apis.length : 2;
    const apiMemory = apis.length ? apis.reduce((sum, e) => sum + e.memoryGib, 0) / apis.length : 1.5;
    const busyInstanceSeconds = (drivers.apiRequests * drivers.apiAvgLatencySeconds) / drivers.apiInFlightPerInstance;
    lines.push(line("runServiceRequests", "CLOUD_RUN", "VARIABLE", drivers.apiRequests, "requests", busyInstanceSeconds * (apiCpu * u("runCpuActive") + apiMemory * u("runMemoryActive")) +
        (drivers.apiRequests / 1_000_000) * u("runRequests"), ["runCpuActive", "runMemoryActive", "runRequests"], "MODELED"));
    const worker = service("worker");
    const workerSeconds = drivers.documentsFetched * drivers.workerSecondsPerDocument;
    lines.push(line("runWorkers", "CLOUD_RUN", "VARIABLE", drivers.documentsFetched, "documents", workerSeconds *
        ((worker?.cpu ?? 1) * u("runCpuActive") + (worker?.memoryGib ?? 1) * u("runMemoryActive")), ["runCpuActive", "runMemoryActive"], usageBasis));
    // Cloud Run jobs: every scheduler tick starts an execution even with no work.
    let tickVcpu = 0;
    let tickGib = 0;
    for (const job of s.jobs) {
        tickVcpu += job.executionsPerMonth * job.idleSecondsPerExecution * job.cpu;
        tickGib += job.executionsPerMonth * job.idleSecondsPerExecution * job.memoryGib;
    }
    lines.push(line("jobsScheduledTicks", "CLOUD_RUN_JOBS", "FIXED", s.jobs.reduce((sum, job) => sum + job.executionsPerMonth, 0), "executions", tickVcpu * u("runJobCpu") + tickGib * u("runJobMemory"), ["runJobCpu", "runJobMemory"], "TERRAFORM"));
    const rowsVcpu = drivers.lotRows * drivers.cpuSecondsPerLotRow;
    lines.push(line("jobsLotRows", "CLOUD_RUN_JOBS", "VARIABLE", drivers.lotRows, "rows", rowsVcpu * u("runJobCpu") + rowsVcpu * 0.5 * u("runJobMemory"), ["runJobCpu", "runJobMemory"], usageBasis));
    const research = s.jobs.find((job) => job.id === "web-research-runner");
    const researchSeconds = drivers.webResearchActiveHours * 3600;
    lines.push(line("jobsWebResearch", "CLOUD_RUN_JOBS", "VARIABLE", drivers.webResearchActiveHours, "hours", researchSeconds *
        ((research?.cpu ?? 2) * u("runJobCpu") + (research?.memoryGib ?? 2) * u("runJobMemory")), ["runJobCpu", "runJobMemory"], usageBasis));
    // Cloud SQL (HA doubles compute and storage).
    const sql = drivers.cloudSqlOverride ?? {
        vcpu: s.cloudSql.vcpu,
        memoryGb: s.cloudSql.memoryGb,
        availability: s.cloudSql.availability,
        diskGb: s.cloudSql.diskGb,
    };
    const ha = sql.availability === "REGIONAL" ? 2 : 1;
    lines.push(line("sqlInstance", "CLOUD_SQL", "FIXED", HOURS_PER_MONTH, "hours", ha * HOURS_PER_MONTH * (sql.vcpu * u("sqlVcpu") + sql.memoryGb * u("sqlMemory")), ["sqlVcpu", "sqlMemory"], drivers.cloudSqlOverride ? "MODELED" : "TERRAFORM"), line("sqlStorage", "CLOUD_SQL", "FIXED", sql.diskGb, "GB-month", ha * sql.diskGb * u("sqlSsd"), ["sqlSsd"], drivers.cloudSqlOverride ? "MODELED" : "TERRAFORM"), line("sqlBackups", "CLOUD_SQL", "FIXED", drivers.sqlBackupGb, "GB-month", drivers.sqlBackupGb * u("sqlBackup"), ["sqlBackup"], "MODELED"));
    // Compute Engine: always-on VMs and their pd-balanced disks.
    for (const vm of s.vms) {
        lines.push(line(vm.id === "browser-agent" ? "vmBrowserAgent" : "vmTwingate", "COMPUTE_ENGINE", "FIXED", HOURS_PER_MONTH, "hours", HOURS_PER_MONTH * u(vm.priceId) + vm.diskGb * u("pdBalanced"), [vm.priceId, "pdBalanced"], "TERRAFORM"));
    }
    // Networking.
    lines.push(line("natGateways", "NETWORKING", "FIXED", s.nat.gateways, "gateways", s.nat.assignedVms * HOURS_PER_MONTH * u("natGateway"), ["natGateway"], "TERRAFORM"), line("externalIps", "NETWORKING", "FIXED", s.externalIps, "addresses", s.externalIps * HOURS_PER_MONTH * u("externalIp"), ["externalIp"], "TERRAFORM"), line("natData", "NETWORKING", "VARIABLE", drivers.natGib, "GiB", drivers.natGib * u("natData"), ["natData"], "MODELED"), line("egress", "NETWORKING", "VARIABLE", drivers.egressGib, "GiB", drivers.egressGib * u("egressInternet"), ["egressInternet"], "MODELED"));
    const gatewayCalls = drivers.apiRequests * drivers.gatewayShare;
    lines.push(line("apiGateway", "API_GATEWAY", "VARIABLE", gatewayCalls, "calls", (gatewayCalls / 1_000_000) * u("gatewayCalls"), ["gatewayCalls"], "MODELED"), line("gcsStorage", "CLOUD_STORAGE", "FIXED", drivers.gcsStorageGb, "GB-month", drivers.gcsStorageGb * u("gcsStandard"), ["gcsStandard"], "MODELED"), line("registryStorage", "ARTIFACT_REGISTRY", "FIXED", drivers.registryGb, "GB-month", drivers.registryGb * u("registryStorage"), ["registryStorage"], "MODELED"), line("pubsub", "PUBSUB", "VARIABLE", drivers.pubsubGib, "GiB", (drivers.pubsubGib / 1024) * u("pubsubThroughput"), ["pubsubThroughput"], "MODELED"), line("scheduler", "CLOUD_SCHEDULER", "FIXED", s.schedulerJobs, "jobs", s.schedulerJobs * u("schedulerJob"), ["schedulerJob"], "TERRAFORM"), line("secretManager", "SECRET_MANAGER", "FIXED", s.secrets.activeVersions, "versions", s.secrets.activeVersions * u("secretVersion") +
        (drivers.secretAccesses / 10_000) * u("secretAccess"), ["secretVersion", "secretAccess"], "TERRAFORM"));
    const billedLogs = Math.max(0, drivers.logsGib - 50);
    lines.push(line("logging", "CLOUD_LOGGING", "VARIABLE", billedLogs, "GiB", billedLogs * u("loggingIngest"), ["loggingIngest"], "MODELED"));
    const billedHosting = Math.max(0, drivers.firebaseTransferGb - 0.36 * DAYS_PER_MONTH);
    lines.push(line("firebaseHosting", "FIREBASE_HOSTING", "VARIABLE", billedHosting, "GB", billedHosting * u("firebaseTransfer"), ["firebaseTransfer"], "MODELED"));
    // Vertex AI (Gemini 2.5 Flash): research and NF-e use the app's recorded spend.
    lines.push(line("geminiResearch", "VERTEX_AI", "VARIABLE", drivers.researchAiUsd, "USD", drivers.researchAiUsd, ["groundedPrompt", "geminiFlashInput", "geminiFlashOutput"], usageBasis), line("geminiNfe", "VERTEX_AI", "VARIABLE", drivers.nfeAiUsd, "USD", drivers.nfeAiUsd, ["geminiFlashInput", "geminiFlashOutput"], usageBasis), line("geminiBrowserAgent", "VERTEX_AI", "VARIABLE", drivers.browserAgentSteps, "steps", (drivers.browserAgentSteps * drivers.browserAgentInputTokensPerStep * u("geminiFlashInput") +
        drivers.browserAgentSteps *
            drivers.browserAgentOutputTokensPerStep *
            u("geminiFlashOutput")) /
        1_000_000, ["geminiFlashInput", "geminiFlashOutput"], usageBasis));
    const fixedUsd = round2(lines.filter((entry) => entry.kind === "FIXED").reduce((sum, entry) => sum + entry.usd, 0));
    const variableUsd = round2(lines.filter((entry) => entry.kind === "VARIABLE").reduce((sum, entry) => sum + entry.usd, 0));
    const totalUsd = round2(fixedUsd + variableUsd);
    return {
        id,
        totalUsd,
        fixedUsd,
        variableUsd,
        totalBrl: round2(totalUsd * fx),
        lines,
        drivers,
    };
}
/* -- Savings ------------------------------------------------------------------- */
export const infraSavingIdSchema = z.enum([
    "apiMinInstancesZero",
    "taskRunnerEveryFiveMinutes",
    "calculatorTickEveryFiveMinutes",
    "browserAgentSmallerVm",
    "registryCleanupPolicy",
    "cloudSqlCommittedUse",
]);
export const infraSavingSchema = z
    .object({
    id: infraSavingIdSchema,
    /** Monthly saving on the TYPICAL scenario, USD. */
    usd: z.number().nonnegative(),
    /** What the change costs in product terms. */
    tradeoff: z.enum(["COLD_START", "SLOWER_PICKUP", "SLOWER_AGENT", "NONE", "COMMITMENT"]),
})
    .strict();
function withSnapshot(patch) {
    const copy = structuredClone(INFRA_TERRAFORM_SNAPSHOT);
    patch(copy);
    return copy;
}
/** Each tip is the TYPICAL estimate re-run with one Terraform value changed. */
export function infraSavings(typical, prices = INFRA_UNIT_PRICES) {
    const rerun = (snapshot, drivers = typical.drivers) => estimateInfraScenario("TYPICAL", drivers, { snapshot, prices }).totalUsd;
    const base = rerun(INFRA_TERRAFORM_SNAPSHOT);
    const diff = (value) => round2(Math.max(0, base - value));
    const cadence = (jobId, everyMinutes) => withSnapshot((snapshot) => {
        const job = snapshot.jobs.find((entry) => entry.id === jobId);
        if (job)
            job.executionsPerMonth = cron(everyMinutes);
    });
    const sqlCompute = typical.lines.find((entry) => entry.id === "sqlInstance")?.usd ?? 0;
    const vm = typical.lines.find((entry) => entry.id === "vmBrowserAgent")?.usd ?? 0;
    const vmDisk = (INFRA_TERRAFORM_SNAPSHOT.vms.find((entry) => entry.id === "browser-agent")?.diskGb ?? 0) *
        prices.pdBalanced.usd;
    return [
        {
            id: "apiMinInstancesZero",
            usd: diff(rerun(withSnapshot((snapshot) => {
                for (const entry of snapshot.services)
                    entry.minInstances = 0;
            }))),
            tradeoff: "COLD_START",
        },
        {
            id: "taskRunnerEveryFiveMinutes",
            usd: diff(rerun(cadence("fiscal-task-runner", 5))),
            tradeoff: "SLOWER_PICKUP",
        },
        {
            id: "calculatorTickEveryFiveMinutes",
            usd: diff(rerun(cadence("calculator-research", 5))),
            tradeoff: "SLOWER_PICKUP",
        },
        {
            // e2-standard-2 is half of e2-standard-4 (same family, same region).
            id: "browserAgentSmallerVm",
            usd: round2((vm - vmDisk) / 2),
            tradeoff: "SLOWER_AGENT",
        },
        {
            // ASSUMPTION: a keep-last-10-per-image cleanup policy holds ~10 GB.
            id: "registryCleanupPolicy",
            usd: diff(rerun(INFRA_TERRAFORM_SNAPSHOT, { ...typical.drivers, registryGb: 10 })),
            tradeoff: "NONE",
        },
        {
            // Cloud SQL 1-year committed use discount: 25% off vCPU and memory.
            id: "cloudSqlCommittedUse",
            usd: round2(sqlCompute * 0.25),
            tradeoff: "COMMITMENT",
        },
    ]
        .filter((saving) => saving.usd > 0)
        .sort((a, b) => b.usd - a.usd);
}
/* -- API response ----------------------------------------------------------------- */
export const infraCostEstimateSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    modelVersion: z.string().min(1),
    region: z.string().min(1),
    pricesCheckedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    generatedAt: z.string().datetime(),
    exchangeRate: z
        .object({
        usdBrl: z.number().positive(),
        rateDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        source: z.string().min(1),
        sourceUrl: z.string().url(),
    })
        .strict(),
    /** Cloud Billing export to BigQuery: not declared in Terraform, so not used. */
    billingExport: z.enum(["NOT_CONFIGURED", "CONFIGURED"]),
    usage: infraLiveUsageSchema,
    scenarios: z.array(infraScenarioEstimateSchema).length(3),
    savings: z.array(infraSavingSchema),
    prices: z.array(infraUnitPriceSchema),
    terraformSource: z.string().min(1),
})
    .strict();
/** The whole response: three scenarios, savings and sources, from the live usage. */
export function buildInfraCostEstimate(input) {
    const live = input.usage.status === "LIVE";
    const scenarios = [
        estimateInfraScenario("IDLE", INFRA_USAGE_PROFILES.IDLE),
        estimateInfraScenario("TYPICAL", typicalDriversFromUsage(input.usage), { recorded: live }),
        estimateInfraScenario("HIGH", highDriversFromUsage(input.usage)),
    ];
    const typical = scenarios[1];
    return {
        schemaVersion: "1.0.0",
        modelVersion: INFRA_COST_MODEL_VERSION,
        region: INFRA_COST_REGION,
        pricesCheckedOn: CHECKED_ON,
        generatedAt: input.generatedAt,
        exchangeRate: { ...INFRA_EXCHANGE_RATE },
        billingExport: "NOT_CONFIGURED",
        usage: input.usage,
        scenarios,
        savings: infraSavings(typical),
        prices: Object.values(INFRA_UNIT_PRICES),
        terraformSource: INFRA_TERRAFORM_SNAPSHOT.source,
    };
}
//# sourceMappingURL=infra-cost.js.map